/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Divider, Form, InputNumber, Modal, Radio, Select, Space, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  useCreateSaveCropMutation,
  useGetSaveCropTaxonomyTreeQuery,
  useGetSaveCropsQuery,
} from '../../api/cropsApi';
import { useCreatePlantingMutation, useGetPlantingsQuery } from '../../api/plantingsApi';
import { useCreatePlantingEventMutation } from '../../api/plantingEventsApi';
import type { PlantingType } from '../../models/Planting';
import dayjs, { type Dayjs } from 'dayjs';
import { useGetSaveWorldQuery } from '../../api/savesV2Api';
import { CropFormModal } from '../crops/CropFormModal';

interface AddPlantingModalProps {
  visible: boolean;
  bedId: string; // CN-0012: UUID-only
  saveId: string;
  farmId: string; // CN-0012: UUID-only
  onCancel: () => void;
  onSuccess?: () => Promise<void> | void;
}

const AddPlantingModal: React.FC<AddPlantingModalProps> = ({
  visible,
  bedId,
  saveId,
  farmId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { data: world } = useGetSaveWorldQuery(saveId, { skip: !visible || !saveId });
  const cursorDate = world?.cursor?.simDate || world?.cursor?.sim_date;
  useEffect(() => {
    if (visible && cursorDate && !form.getFieldValue('fromDate')) {
      form.setFieldsValue({ fromDate: dayjs(cursorDate) });
    }
  }, [visible, cursorDate, form]);
  const [plantingType, setPlantingType] = useState<PlantingType>('single');
  const [sourceType, setSourceType] = useState<'new' | 'storage'>('new');
  const [cropCatalogOpen, setCropCatalogOpen] = useState(false);
  
  const {
    data: crops = [],
    isLoading: loadingCrops,
    refetch: refetchCrops,
  } = useGetSaveCropsQuery({ saveId, limit: 1000, offset: 0 }, { skip: !visible || !saveId });
  const {
    data: taxonomy,
    isLoading: taxonomyLoading,
  } = useGetSaveCropTaxonomyTreeQuery(saveId, { skip: !saveId || (!visible && !cropCatalogOpen) });
  const { data: farmPlantings = [], isLoading: loadingStoragePlantings } = useGetPlantingsQuery(
    {
      saveId,
      limit: 1000,
      offset: 0,
    },
    { skip: !visible }
  );
  const [createCrop, { isLoading: creatingCrop }] = useCreateSaveCropMutation();
  const [createPlanting, { isLoading: creating }] = useCreatePlantingMutation();
  const [createPlantingEvent, { isLoading: movingFromStorage }] = useCreatePlantingEventMutation();

  // Посадки в хранилище: активная локация без грядки (реальный holding или
  // виртуальный sim-перенос со статусом active). Терминальные статусы исключаем.
  const storagePlantings = useMemo(
    () =>
      farmPlantings.filter(
        (planting) =>
          planting.inStorage
          && !['dead', 'sold', 'archived'].includes(String(planting.lifecycleStatus)),
      ),
    [farmPlantings],
  );

  const handleCreateCropFromCatalog = async (values: any) => {
    if (!farmId) return;
    if (values.speciesId === undefined || values.speciesId === null) {
      message.warning('Выберите вид');
      return;
    }

    try {
      const created = await createCrop({
        saveId,
        farmId,
        speciesId: Number(values.speciesId),
        cultivarId:
          values.cultivarId === undefined || values.cultivarId === null
            ? null
            : Number(values.cultivarId),
        latinName: values.latinName,
        name: values.name,
      }).unwrap();
      await refetchCrops();
      form.setFieldsValue({ cropId: created.id });
      setCropCatalogOpen(false);
      message.success('Культура добавлена и выбрана');
    } catch (error: any) {
      message.error(error?.data?.message || 'Не удалось добавить культуру');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const fromDate = values.fromDate
        ? (values.fromDate as Dayjs).format('YYYY-MM-DD')
        : new Date().toISOString().split('T')[0];

      if (sourceType === 'storage') {
        await createPlantingEvent({
          saveId,
          plantingId: String(values.storagePlantingId),
          payload: {
            kind: 'transplant',
            properties: {
              transplanting: JSON.stringify({
                bedId,
                fromDate,
              }),
            },
          },
        }).unwrap();

        message.success('Посадка успешно размещена со склада');
        form.resetFields();
        try {
          await onSuccess?.();
        } catch (refreshError) {
          console.error('[AddPlantingModal] post-success refresh failed:', refreshError);
        }
        onCancel();
        return;
      }
      
      const payload = {
        cropId: values.cropId,
        plantingType,
        quantity: plantingType === 'batch' ? values.quantity : 1,
        locations: [{
          bedId,
          fromDate,
        }],
        lifecycleStatus: 'active' as const,
      };

      await createPlanting({ ...payload, saveId }).unwrap();
      message.success('Посадка успешно создана');
      form.resetFields();
      try {
        await onSuccess?.();
      } catch (refreshError) {
        console.error('[AddPlantingModal] post-success refresh failed:', refreshError);
      }
      onCancel();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.data?.message || 'Ошибка создания посадки');
    }
  };

  return (
    <Modal
      title="Добавить посадку"
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={sourceType === 'storage' ? 'Разместить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={creating || movingFromStorage}
      width={560}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          sourceType: 'new',
          plantingType: 'single',
          quantity: 1,
        }}
      >
        <Form.Item name="sourceType" label="Источник посадки">
          <Radio.Group
            value={sourceType}
            onChange={(e) => {
              setSourceType(e.target.value);
              form.setFieldsValue({ cropId: undefined, storagePlantingId: undefined });
            }}
          >
            <Radio value="new">Создать новую</Radio>
            <Radio value="storage">Выбрать со склада</Radio>
          </Radio.Group>
        </Form.Item>

        {sourceType === 'new' && (
          <>
            <Form.Item
              label="Культура"
              required
            >
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  name="cropId"
                  noStyle
                  rules={[{ required: true, message: 'Выберите культуру' }]}
                >
                  <Select
                    placeholder="Выберите культуру"
                    loading={loadingCrops}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => setCropCatalogOpen(true)}
                        >
                          Добавить из каталога
                        </Button>
                      </>
                    )}
                    options={crops.map(crop => ({
                      value: crop.id,
                      label: crop.cultivarName
                        ? `${crop.name} — ${crop.cultivarName}`
                        : (crop.latinName || crop.name || `Crop #${crop.id}`),
                    }))}
                  />
                </Form.Item>
                <Button icon={<PlusOutlined />} onClick={() => setCropCatalogOpen(true)}>
                  Из каталога
                </Button>
              </Space.Compact>
            </Form.Item>

            <Form.Item label="Тип посадки">
              <Radio.Group
                value={plantingType}
                onChange={(e) => setPlantingType(e.target.value)}
              >
                <Radio value="single">Единичная</Radio>
                <Radio value="batch">Групповая</Radio>
              </Radio.Group>
            </Form.Item>

            {plantingType === 'batch' && (
              <Form.Item
                name="quantity"
                label="Количество растений"
                rules={[
                  { required: true, message: 'Укажите количество' },
                  { type: 'number', min: 1, message: 'Минимум 1' },
                ]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            )}
          </>
        )}

        {sourceType === 'storage' && (
          <Form.Item
            name="storagePlantingId"
            label="Посадка со склада"
            rules={[
              { required: true, message: 'Выберите посадку со склада' },
            ]}
          >
            <Select
              placeholder="Выберите посадку"
              loading={loadingStoragePlantings}
              showSearch
              optionFilterProp="label"
              options={storagePlantings.map((planting) => ({
                value: String(planting.id),
                label: `${planting.crop.name}${
                  planting.plantingType === 'batch' ? ` (${planting.quantity || 1} шт.)` : ''
                }`,
              }))}
            />
          </Form.Item>
        )}

        <Form.Item
          name="fromDate"
          label="Дата размещения"
          rules={[{ required: true, message: 'Выберите дату' }]}
        >
          <DatePicker disabledDate={date => !!cursorDate && date.isBefore(dayjs(cursorDate), 'day')} style={{ width: '100%' }} format="DD.MM.YYYY" />
        </Form.Item>
      </Form>
      <CropFormModal
        open={cropCatalogOpen}
        editingCrop={null}
        taxonomy={taxonomy}
        taxonomyLoading={taxonomyLoading}
        onSubmit={handleCreateCropFromCatalog}
        onCancel={() => setCropCatalogOpen(false)}
        isLoading={creatingCrop}
      />
    </Modal>
  );
};

export default AddPlantingModal;
